// HEIC and HEIF photos, which iPhones take by default. Safari and the macOS and iOS apps decode
// them natively. Elsewhere libheif, built to WebAssembly, decodes them on this device. It is about
// 2 MB, so it loads only when the first HEIC photo is sent.

import type { Picture, Raster } from "./raster";

type HeifImage = {
  get_width(): number;
  get_height(): number;
  display(
    target: { data: Uint8ClampedArray; width: number; height: number },
    done: (out: { data: Uint8ClampedArray } | null) => void,
  ): void;
  free?(): void;
};
type HeifModule = { HeifDecoder: new () => { decode(bytes: Uint8Array): HeifImage[] } };

let loading: Promise<HeifModule> | null = null;

function libheif(): Promise<HeifModule> {
  loading ??= import("libheif-js/wasm-bundle").then(
    (m) => ((m as { default?: HeifModule }).default ?? m) as HeifModule,
  );
  return loading;
}

/** Decodes the primary picture of a HEIC file with libheif. Rotation and mirroring are applied. */
export async function decodeHeicPixels(
  file: Blob,
): Promise<{ data: Uint8ClampedArray; width: number; height: number }> {
  const lib = await libheif();
  const images = new lib.HeifDecoder().decode(new Uint8Array(await file.arrayBuffer()));
  const image = images[0];
  if (!image) throw new Error("This HEIC photo has no picture in it.");
  try {
    const width = image.get_width();
    const height = image.get_height();
    const target = { data: new Uint8ClampedArray(width * height * 4), width, height };
    const out = await new Promise<{ data: Uint8ClampedArray }>((resolve, reject) =>
      image.display(target, (d) =>
        d ? resolve(d) : reject(new Error("This HEIC photo could not be read.")),
      ),
    );
    return { data: out.data, width, height };
  } finally {
    for (const im of images) im.free?.();
  }
}

/** A HEIC photo as a picture: the platform's own decoder first, libheif when it has none. */
export async function decodeHeic(file: Blob, raster: Raster): Promise<Picture> {
  try {
    return await raster.decode(file);
  } catch {
    const { data, width, height } = await decodeHeicPixels(file);
    return raster.fromPixels(data, width, height);
  }
}
